import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Mail, Phone, Calendar, CreditCard, ShieldAlert, ImageIcon, Download,
  Maximize2, CheckCircle2, XCircle, DollarSign, User,
} from "lucide-react";

const RATE = { submitted: "received", under_review: "under review", approved: "approved", paid: "paid", rejected: "rejected", duplicate: "duplicate" };
const StatusChip = ({ s }) => {
  const map = {
    submitted: "bg-sky-100 text-sky-700",
    under_review: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    paid: "bg-emerald-500 text-white",
    rejected: "bg-rose-100 text-rose-700",
    duplicate: "bg-slate-200 text-slate-700",
  };
  return <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide ${map[s] || "bg-slate-100"}`}>{RATE[s] || s}</span>;
};

const Field = ({ icon: Icon, label, value, mono, valueClass }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0"><Icon size={14} /></div>
    <div className="min-w-0 flex-1">
      <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-sm font-bold text-slate-800 break-words ${valueClass || ""}`}>{value || "—"}</div>
    </div>
  </div>
);

export default function SubmissionDetailDrawer({ id, onClose, onChanged }) {
  const [sub, setSub] = useState(null);
  const [note, setNote] = useState("");
  const [finalPayout, setFinalPayout] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  const load = () => adminApi.get(`/admin/submissions/${id}`).then((r) => {
    setSub(r.data);
    setFinalPayout(r.data.final_payout || r.data.estimated_payout || "");
  });

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onEsc); document.body.style.overflow = ""; };
  }, [onClose]);

  const changeStatus = async (newStatus) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/submissions/${id}/status`, {
        status: newStatus,
        note,
        final_payout: newStatus === "paid" ? Number(finalPayout) : undefined,
      });
      toast.success(`Status → ${newStatus.replace("_", " ")}`);
      setNote("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!sub) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex justify-end">
        <div className="w-full max-w-3xl bg-white h-full flex items-center justify-center">Loading…</div>
      </div>
    );
  }

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const rate = sub.card_amount ? Math.round((sub.estimated_payout / sub.card_amount) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-300"
        data-testid="submission-detail-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-5 flex items-center justify-between">
          <h2 className="text-2xl font-black text-slate-900">Submission Details</h2>
          <button onClick={onClose} data-testid="drawer-close" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-900 hover:text-white flex items-center justify-center transition">
            <X size={16} />
          </button>
        </div>

        <div className="p-8 space-y-6">
          {/* Reference + status */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">Reference Number</div>
              <div className="mt-1 text-2xl font-black">
                <span className="text-indigo-600">{sub.reference?.split("-").slice(0, 2).join("-")}</span>
                <span className="text-slate-800">-{sub.reference?.split("-")[2]}</span>
              </div>
            </div>
            <StatusChip s={sub.status} />
          </div>

          {/* Customer grid */}
          <div className="rounded-2xl border border-slate-200 p-6 grid sm:grid-cols-2 gap-5">
            <Field icon={User} label="Customer" value={sub.customer_name || "—"} />
            <Field icon={Mail} label="Email" value={sub.customer_email} />
            <Field icon={Phone} label="Phone" value={sub.customer_phone} />
            <Field icon={Calendar} label="Submitted" value={new Date(sub.created_at).toLocaleString()} />
          </div>

          {/* Gift card box */}
          <div className="rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
              <CreditCard size={16} className="text-slate-500" />
              <h3 className="font-black text-slate-900 text-sm">Gift Cards</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="font-black text-slate-900">Card 1: {sub.brand_name} ({sub.card_type || "physical"})</div>
                <div className="text-lg font-black text-slate-900">${sub.card_amount}</div>
              </div>

              {/* Sensitive info */}
              <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50/50 p-5">
                <div className="flex items-center gap-2 text-rose-700 mb-4">
                  <ShieldAlert size={14} />
                  <span className="text-[10px] font-black uppercase tracking-wider">Sensitive Card Information</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Card Number:</span> <span className="font-mono font-bold text-slate-900" data-testid="drawer-card-number">{sub.card_number || "—"}</span></div>
                  <div><span className="text-slate-500">PIN / CVV:</span> <span className="font-mono font-bold text-slate-900">{sub.card_pin || "—"}</span></div>
                  <div><span className="text-slate-500">Expiry:</span> <span className="font-bold text-slate-900">{sub.card_expiry || "—"}</span></div>
                  <div><span className="text-slate-500">Condition:</span> <span className="font-bold text-slate-900 capitalize">{sub.card_condition || "unused"}</span></div>
                  <div><span className="text-slate-500">Receipt:</span> <span className="font-bold text-slate-900">{sub.has_receipt ? "Yes" : "No"}</span></div>
                  <div><span className="text-slate-500">Rate:</span> <span className="font-bold text-slate-900">{rate}%</span></div>
                  <div className="col-span-2 pt-2 mt-2 border-t border-rose-200 flex items-center justify-between">
                    <span className="text-slate-500">Payout:</span>
                    <span className="font-black text-emerald-600 text-lg">${sub.estimated_payout?.toFixed?.(2) ?? sub.estimated_payout}</span>
                  </div>
                </div>
              </div>

              {/* Card images */}
              {sub.photo_urls?.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 text-slate-500 mb-3">
                    <ImageIcon size={14} />
                    <span className="text-[10px] font-black uppercase tracking-wider">Card Images</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {sub.photo_urls.slice(0, 4).map((u, i) => {
                      const url = u.startsWith("http") ? u : `${backend}${u}`;
                      const label = i === 0 ? "Card Front" : i === 1 ? "Card Back" : `Photo ${i + 1}`;
                      return (
                        <div key={i} className="rounded-xl border border-slate-200 overflow-hidden">
                          <div className="px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-100">{label}</div>
                          <div className="p-3">
                            <div className="relative w-full h-40 rounded-md bg-slate-100 overflow-hidden">
                              <img
                                src={url}
                                alt={label}
                                className="absolute inset-0 w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const sib = e.currentTarget.nextElementSibling;
                                  if (sib) sib.style.display = "flex";
                                }}
                              />
                              <div
                                className="absolute inset-0 hidden flex-col items-center justify-center gap-1 text-slate-400 text-xs font-semibold p-3 text-center"
                                data-testid={`card-image-missing-${i}`}
                              >
                                <ImageIcon size={22} />
                                <div>Image no longer available</div>
                                <div className="text-[10px] text-slate-400 font-normal">Uploaded before persistent storage was enabled</div>
                              </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                              <button onClick={() => setPreviewImg(url)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">
                                <Maximize2 size={12} /> View Full
                              </button>
                              <a href={url} download target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">
                                <Download size={12} /> Download
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payout method */}
          <div className="rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-slate-500 mb-3">
              <DollarSign size={14} />
              <span className="text-[10px] font-black uppercase tracking-wider">Payment Method</span>
            </div>
            <div className="font-black text-slate-900">{sub.payout_method}</div>
            <div className="text-sm text-slate-600 font-mono">
              {typeof sub.payout_details === "object"
                ? Object.entries(sub.payout_details || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")
                : String(sub.payout_details || "")}
            </div>
          </div>

          {/* Fraud flags */}
          <div className="rounded-2xl border border-slate-200 p-6 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">Fraud Score</div>
              <div className="text-3xl font-black text-slate-900 mt-1">{sub.fraud_flags?.length || 0}</div>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">Risk Level</div>
              <div className="mt-1">
                {sub.fraud_flags?.length > 0
                  ? <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 font-black text-xs">HIGH</span>
                  : <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-black text-xs">LOW</span>}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="rounded-2xl border border-slate-200 p-6">
            <h3 className="font-black text-slate-900 mb-4">Status Timeline</h3>
            <ol className="space-y-4 border-l-2 border-slate-100 pl-6 relative">
              {(sub.timeline || []).map((t, i) => (
                <li key={i} className="relative">
                  <div className="absolute -left-[29px] top-1.5 w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-indigo-100" />
                  <div className="text-xs text-slate-400">{new Date(t.at).toLocaleString()} · {t.by}</div>
                  <div className="font-black capitalize text-slate-800">{t.status?.replace("_", " ")}</div>
                  {t.note && <div className="text-sm text-slate-600 mt-0.5">{t.note}</div>}
                </li>
              ))}
              {!sub.timeline?.length && <li className="text-sm text-slate-400">No history yet.</li>}
            </ol>
          </div>

          {/* Actions */}
          <div className="rounded-2xl border border-slate-200 p-6 sticky bottom-0 bg-white">
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" data-testid="drawer-note" rows={2} className="w-full rounded-lg border border-slate-200 p-2.5 text-sm resize-none focus:outline-none focus:border-indigo-400" />
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Final payout $</label>
                <input value={finalPayout} onChange={(e) => setFinalPayout(e.target.value)} type="number" step="0.01" data-testid="drawer-final-payout" className="mt-1 w-full rounded-lg border border-slate-200 p-2.5 text-sm font-mono focus:outline-none focus:border-indigo-400" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button disabled={busy} onClick={() => changeStatus("approved")} data-testid="drawer-approve" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm disabled:opacity-50 transition">
                <CheckCircle2 size={16} /> Approve
              </button>
              <button disabled={busy} onClick={() => changeStatus("rejected")} data-testid="drawer-reject" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-sm disabled:opacity-50 transition">
                <XCircle size={16} /> Reject
              </button>
              <button disabled={busy} onClick={() => changeStatus("paid")} data-testid="drawer-mark-paid" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-black text-sm disabled:opacity-50 transition">
                <DollarSign size={16} /> Mark Paid
              </button>
            </div>
          </div>
        </div>
      </div>

      {previewImg && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6" onClick={() => setPreviewImg(null)}>
          <img src={previewImg} alt="preview" className="max-h-full max-w-full rounded-xl" />
          <button onClick={() => setPreviewImg(null)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center"><X /></button>
        </div>
      )}
    </div>
  );
}
